import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Zap, Tv, Target } from "lucide-react";

const heroVideos = [
  "/hero-video-1.mp4",
  "/hero-video-2.mp4",
  "/innovation-lab-bg.mp4",
];

const marqueeItems = [
  "SOCIAL MEDIA", "TV VISIBILITY", "INNOVATION LAB", "SEO",
  "REPUTATION", "GRAPHIC DESIGN", "GOOGLE ADS", "VIDEO PRODUCTION",
  "BREVETTI", "MOBILE APP", "BRANDING", "BLOCKCHAIN"
];

export default function HeroSection() {
  const [currentVideo, setCurrentVideo] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentVideo((prev) => (prev + 1) % heroVideos.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <section data-testid="hero-section" className="relative min-h-[85vh] sm:min-h-screen flex items-center justify-center overflow-hidden grain-overlay">
        {/* Video carousel background */}
        {heroVideos.map((src, i) => (
          <video
            key={src}
            autoPlay
            muted
            loop
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ${
              i === currentVideo ? "opacity-30" : "opacity-0"
            }`}
          >
            <source src={src} type="video/mp4" />
          </video>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-transparent to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/70 via-transparent to-[#050505]/50" />

        {/* Video indicator dots */}
        <div className="absolute bottom-24 sm:bottom-20 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {heroVideos.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentVideo(i)}
              className={`w-8 sm:w-12 h-1 transition-all duration-500 ${
                i === currentVideo ? "bg-[#FF3366]" : "bg-white/20"
              }`}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 w-full pt-20 sm:pt-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="mb-3 sm:mb-5">
            <span className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-[#FF3366] uppercase">
              Innovazione Digitale & Tecnologia
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-[clamp(3.5rem,12vw,11rem)] font-black tracking-tighter leading-[0.85] uppercase"
          >
            NINFA
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-[clamp(3.5rem,12vw,11rem)] font-heading font-black tracking-tighter leading-[0.85] text-stroke select-none"
          >
            .EU
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="text-sm sm:text-lg text-[#D0D0D0] font-body leading-relaxed mt-5 sm:mt-8 max-w-lg"
          >
            Innovazione digitale, brevetti, piattaforme e soluzioni tecnologiche per far crescere il tuo business.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="mt-5 sm:mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a href="#servizi" data-testid="hero-cta-services" className="bg-[#FF3366] text-white font-bold uppercase tracking-widest px-6 sm:px-8 py-3.5 text-xs sm:text-sm hover:bg-white hover:text-black transition-colors duration-300 text-center">
              SCOPRI I SERVIZI
            </a>
            <a href="#contatti" data-testid="hero-cta-contact" className="border border-white/20 text-white font-mono text-xs sm:text-sm px-6 sm:px-8 py-3.5 hover:bg-white hover:text-black transition-colors duration-300 tracking-wider text-center">
              CONTATTACI
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="mt-8 sm:mt-14 grid grid-cols-3 gap-2 sm:gap-4 max-w-lg"
          >
            <div data-testid="hero-stat-users" className="border border-white/10 bg-black/30 backdrop-blur-sm p-2.5 sm:p-4">
              <Zap size={14} className="text-[#FF3366] mb-1.5 sm:mb-2" />
              <p className="font-heading text-base sm:text-2xl font-black text-white">7.9 MLN</p>
              <p className="text-[8px] sm:text-[10px] font-mono text-[#C0C0C0] tracking-wider mt-0.5">UTENTI</p>
            </div>
            <div data-testid="hero-stat-tv" className="border border-white/10 bg-black/30 backdrop-blur-sm p-2.5 sm:p-4">
              <Tv size={14} className="text-[#FF3366] mb-1.5 sm:mb-2" />
              <p className="font-heading text-base sm:text-2xl font-black text-white">TV</p>
              <p className="text-[8px] sm:text-[10px] font-mono text-[#C0C0C0] tracking-wider mt-0.5">VISIBILITA</p>
            </div>
            <div data-testid="hero-stat-360" className="border border-white/10 bg-black/30 backdrop-blur-sm p-2.5 sm:p-4">
              <Target size={14} className="text-[#FF3366] mb-1.5 sm:mb-2" />
              <p className="font-heading text-base sm:text-2xl font-black text-white">360</p>
              <p className="text-[8px] sm:text-[10px] font-mono text-[#C0C0C0] tracking-wider mt-0.5">MARKETING</p>
            </div>
          </motion.div>
        </div>

        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 z-20">
          <ArrowDown size={18} className="text-white/30" />
        </motion.div>
      </section>

      {/* Marquee */}
      <div data-testid="marquee-section" className="bg-[#FF3366] py-2.5 sm:py-3 overflow-hidden w-full max-w-full">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="font-mono text-[10px] sm:text-sm font-bold tracking-[0.15em] text-white mx-3 sm:mx-6">
              {item} <span className="text-white/40 mx-1.5 sm:mx-3">/</span>
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
